import { useState, useEffect } from 'react'
import { MotionConfig } from 'framer-motion'
import Hero from './components/Hero'
import About from './components/About'
import Experience from './components/Experience'
import Projects from './components/Projects'
import Education from './components/Education'
import Skills from './components/Skills'
import Achievements from './components/Achievements'
import Contact from './components/Contact'
import Navigation from './components/Navigation'
import Footer from './components/Footer'
import { PortfolioData } from './types'
import Articles from './components/Articles'

function App() {
  const [data, setData] = useState<PortfolioData | null>(null)
  const [darkMode, setDarkMode] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for saved theme preference or default to light mode
    const savedTheme = localStorage.getItem('theme')
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      setDarkMode(true)
      document.documentElement.classList.add('dark')
    }
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/data.json')
        const portfolioData = await response.json()
        setData(portfolioData)
      } catch (error) {
        console.error('Error loading portfolio data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode
    setDarkMode(newDarkMode)

    if (newDarkMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Error Loading Portfolio</h1>
          <p className="text-gray-600 dark:text-gray-400">Please try refreshing the page.</p>
        </div>
      </div>
    )
  }

  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
        {/* Navigation */}
        <Navigation darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

        {/* Main Content */}
        <main>
          <Hero data={data.profile} socials={data.socials} />
          <About data={data.profile} />
          <Experience experiences={data.experience} />
          <Projects projects={data.projects} />
          <Articles articles={data.articles} />
          <Education education={data.education} organizations={data.organizations} />
          <Skills skills={data.skills} />
          <Achievements achievements={data.achievements} />
          <Contact profile={data.profile}/>
        </main>
        <Footer profile={data.profile} socials={data.socials} />
      </div>
    </MotionConfig>
  )
}

export default App
